import { Grid } from "@mui/material";
import { MprcChipChoice, MprcFormSubHeader, MprcRequestIndicator } from "components";
import { MprcDatePicker } from "components/MprcDatePicker";
import { GetPublicationStickerDto, SelectedStickerDto, StickerAppliesTo, StickerDto } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface IStickerBoxValidation {
    viewStickerUntil?: boolean;
}

export interface IStickerDataProps {
    sticker: GetPublicationStickerDto | null;
    viewStickerUntil: Date | null;
}

interface IProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    applyTo: StickerAppliesTo;
    value: SelectedStickerDto | null;
    minStartDate?: Date;
    maxStartDate?: Date;
    onChange: (sticker: SelectedStickerDto | null, validate: boolean) => void;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const DatePickerMaxDate = new Date('2100-01-01');
const DatePickerMinDate = new Date('1900-01-01');

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const StickerBox = (props: IProps) => {

    const { title, subtitle, value, applyTo, minStartDate, maxStartDate, onChange } = props;
    const { t } = useTranslation("knowledge");

    const api = useCoreApi();

    const [selectedSticker, setSelectedSticker] = useState<SelectedStickerDto | null>();
    const [stickers, setStickers] = useState<StickerDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [validation, setValidation] = useState<boolean>(true);

    ///
    const requiredDeadLine = useMemo(
        () => stickers.find(p => p.stickerId == selectedSticker?.stickerId)?.requiredDeadline ?? false
        , [selectedSticker]
    );

    ///
    const updateSticker = (data: StickerDto | null) => {
        if (data == null) setSelectedSticker(null);
        else setSelectedSticker((state) => ({ ...state!, ...data }));
    };

    ///
    const updateViewStickerUntil = (date: Date | null) => {
        setSelectedSticker({ ...selectedSticker, viewUntil: date } as SelectedStickerDto);
    };

    ///
    useDidUpdateEffect(() => {
        if (selectedSticker != null && (selectedSticker.stickerId != value?.stickerId || selectedSticker.viewUntil != value?.viewUntil))
            onChange(
                { stickerId: selectedSticker.stickerId, viewUntil: selectedSticker.viewUntil } as SelectedStickerDto
                , validation && (requiredDeadLine == false || selectedSticker.viewUntil != null)
            );
        else if (selectedSticker === null)
            onChange(null, true);

    }, [selectedSticker]);

    ///
    useEffect(() => {
        if (selectedSticker != undefined)
            onChange(
                { stickerId: selectedSticker.stickerId, viewUntil: selectedSticker.viewUntil } as SelectedStickerDto
                , validation && (requiredDeadLine == false || selectedSticker.viewUntil != null)
            );
    }, [validation]);

    ///
    useEffect(() => {
        async function load() {
            try {
                setLoading(true);
                const result = await api.v1.sticker.availableStickers({ ApplyTo: applyTo });
                setStickers(result.data);
            }
            catch {
                setStickers([]);
            }
            finally {
                setLoading(false);
            }
        }
        load();
    }, []);


    /// Ao mudar o valor selecionado
    useEffect(() => {
        setSelectedSticker(value ?? null);
    }, [value]);

    ///
    if (!loading && stickers.length === 0)
        return null;

    ///
    return (
        <>
            <MprcFormSubHeader
                margin="dense"
                title={title}
                subtitle={subtitle}
            />

            <Grid container justifyContent="flex-start" spacing={0.5} direction="column" mt={.5}>
                <Grid>
                    {
                        loading
                            ? <MprcRequestIndicator />
                            : (
                                <>
                                    {
                                        stickers.map((item) => (
                                            <MprcChipChoice
                                                key={item.stickerId}
                                                label={item.name}
                                                selected={item.stickerId == value?.stickerId}
                                                onClick={() => updateSticker(item)}
                                                onDelete={
                                                    item.stickerId == value?.stickerId
                                                        ? () => setSelectedSticker(null)
                                                        : undefined
                                                }
                                            />
                                        ))
                                    }
                                </>
                            )
                    }
                </Grid>
                {selectedSticker != null &&
                    <Grid>
                        <Grid container>
                            <Grid>
                                <MprcDatePicker
                                    label={Formaters.capitalize(t("Publication.StickerBox.DeadlineLabel"))}
                                    timePartHour="endDate"
                                    fullWidth
                                    required={requiredDeadLine}
                                    minDate={minStartDate || DatePickerMinDate}
                                    maxDate={maxStartDate || DatePickerMaxDate}
                                    value={selectedSticker?.viewUntil ?? null}
                                    error={(selectedSticker?.viewUntil == null && requiredDeadLine)}
                                    onChange={(date) => updateViewStickerUntil(date)}
                                    onError={(reason) => setValidation(reason === null)}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
        </>
    );
};
